import apiClient from "./api-client";
import { customStorage } from "@/firebase";
import { ref, uploadBytesResumable } from "firebase/storage";

export interface UploadUrlResponse {
  url: string;
  object_path: string;
  recording_id: string;
  expires_in_seconds: number;
}

export interface ConvertVideoArgs {
  orgId: string;
  objectPath: string;
  durationMs: number;
  fps: number;
  hasAudio: boolean;
  mimeType?: string;
  filename?: string;
}

function createRecordingId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createRecordingUploadTarget(orgId: string): UploadUrlResponse {
  const recordingId = createRecordingId();
  return {
    url: "",
    object_path: `test-media/${orgId}/recordings/${recordingId}.webm`,
    recording_id: recordingId,
    expires_in_seconds: 0,
  };
}

/**
 * Step 1 of the upload flow: ask the backend for a short-lived signed
 * PUT URL targeting `test-recordings/<org_id>/<recording_id>.webm`.
 */
export async function requestRecordingUploadUrl(
  orgId: string
): Promise<UploadUrlResponse> {
  const response = await apiClient.post<UploadUrlResponse>(
    "/test_recordings/upload_url",
    { org_id: orgId }
  );
  return response.data;
}

/**
 * Step 2: upload the recorded blob to Firebase Storage.
 *
 * Recorder uploads live under `test-media/` because that is already a
 * client-writable namespace used by the test editor.
 */
export function putRecordingToStorage({
  objectPath,
  blob,
  contentType = "video/webm",
  onProgress,
  signal,
}: {
  url?: string;
  objectPath: string;
  blob: Blob;
  contentType?: string;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(
      ref(customStorage, objectPath),
      blob,
      { contentType }
    );

    if (signal) {
      if (signal.aborted) {
        uploadTask.cancel();
        return;
      }
      signal.addEventListener("abort", () => uploadTask.cancel(), {
        once: true,
      });
    }

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        if (snapshot.totalBytes > 0) {
          onProgress?.(snapshot.bytesTransferred / snapshot.totalBytes);
        }
      },
      (error) => {
        if (error.code === "storage/canceled") {
          reject(new DOMException("Upload aborted", "AbortError"));
          return;
        }
        reject(error);
      },
      () => {
        onProgress?.(1);
        resolve();
      }
    );
  });
}

/**
 * Step 3: ask the backend to download the video from GCS and run it
 * through Gemini, returning the markdown test definition. This shares
 * the existing /convert_to_markdown endpoint with the text/csv/image
 * import flows so the output schema stays consistent.
 */
export async function convertVideoToMarkdown({
  orgId,
  objectPath,
  durationMs,
  fps,
  hasAudio,
  mimeType = "video/webm",
  filename,
}: ConvertVideoArgs): Promise<string> {
  const response = await apiClient.post<{ markdown: string }>(
    "/convert_to_markdown",
    {
      org_id: orgId,
      content_type: "video",
      video_object_path: objectPath,
      duration_ms: durationMs,
      fps,
      has_audio: hasAudio,
      mime_type: mimeType,
      filename,
    },
    // The backend's Gemini call can take 60-90s; give it real headroom.
    { timeout: 240_000 }
  );
  return response.data?.markdown ?? "";
}
